import { prefectureMap } from 'src/libs/prefectureMap'

export const isServer = typeof window === 'undefined'

/** 引数の文字列がURLの場合true */
export const isValidURL = (s: string): boolean => {
  const regex = new RegExp(
    '^(https?:\\/\\/)' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name and extension
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?' + // port
      '(\\/[-a-z\\d%_.~+]*)*' + // path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i'
  )
  return regex.test(s)
}

/**
 * sizeを表示する上で正しい単位の文字列として返す
 * - 515899 → 503.81 KB
 * - 2723229 → 2.6 MB
 */
export const getFileSize = (size: number): string => {
  const kb = 1024
  const mb = kb ** 2
  const gb = kb ** 3
  const tb = kb ** 4
  const pb = kb ** 5
  const roundSize = (s: number, unit: number) => {
    return Math.round((s / unit) * 100) / 100
  }

  if (size >= pb) {
    return `${roundSize(size, pb)} PB`
  }
  if (size >= tb) {
    return `${roundSize(size, tb)} TB`
  }
  if (size >= gb) {
    return `${roundSize(size, gb)} GB`
  }
  if (size >= mb) {
    return `${roundSize(size, mb)} MB`
  }
  if (size >= kb) {
    return `${roundSize(size, kb)} KB`
  }
  return `${size} byte`
}

/** 第一、第二引数の配列が内容の順番も含め同じ場合trueを返す */
export const isArraysAreEqual = (a1: any[], a2: any[]): boolean => {
  if (a1.length !== a2.length) return false
  for (const [index, element] of a1.entries()) {
    if (element !== a2[index]) return false
  }
  return true
}

/**
 * 与えられたオブジェクトから、`undefined` または `null` の値を持つプロパティを取り除いた新しいオブジェクトを返す
 *
 * @param object - 入力オブジェクト
 * @returns `undefined` または `null` の値を持つプロパティを除いた新しいオブジェクト
 */
export const cleanObject = <T extends object>(object: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(object).filter(([_, value]) => value !== undefined && value !== null)
  ) as Partial<T>

const sortArray = <T>(array: T[]): T[] => {
  return [...array].sort((a, b) => {
    if (typeof a === 'number' && typeof b === 'number') {
      return a - b
    }
    if (typeof a === 'object' && a?.toString && typeof b === 'object' && b?.toString) {
      return a.toString().localeCompare(b.toString())
    }
    return 0 // その他の型についてはデフォルトのソートを適用しない
  })
}

/**
 * isSameElementsIncluded: 2つの配列が全く同じ要素を含むかを判定する
 * @param array1 - 第1の配列
 * @param array2 - 第2の配列
 * @returns 2つの配列が同じ要素を含む場合はtrue、そうでない場合はfalse
 */
export const isSameElementsIncluded = <T>(array1: T[], array2: T[]): boolean => {
  if (array1.length !== array2.length) {
    return false
  }

  const sortedArray1 = sortArray(array1)
  const sortedArray2 = sortArray(array2)

  for (const [index, element] of sortedArray1.entries()) {
    if (element !== sortedArray2[index]) {
      return false
    }
  }
  return true
}

export const unique = <T>(array: T[]): T[] => {
  return [...new Set(array)]
}

/**
 * 2つの配列が同じ内容を持っているかどうかを確認する。
 * 順番は考慮しない。ただし、プリミティブなデータ型にのみ適用可能。
 *
 * @param array1 - 1つ目の配列
 * @param array2 - 2つ目の配列
 * @returns 2つの配列が同じ内容を持っていればtrue、そうでなければfalse
 */
export const arraysHaveSameContent = <T>(array1: T[], array2: T[]): boolean => {
  // 2つの配列の長さが等しいか確認
  if (array1.length !== array2.length) {
    return false
  }

  // 配列をソート（元の配列を変更せずに新しい配列としてソート）
  const sortedArray1 = [...array1].sort()
  const sortedArray2 = [...array2].sort()

  // ソートされた2つの配列を要素ごとに比較
  return sortedArray1.every((value, index) => value === sortedArray2[index])
}

export const base64Encode = (string_: string): string => {
  return btoa(
    encodeURIComponent(string_).replace(/%([\dA-F]{2})/g, (match, p1) =>
      String.fromCodePoint(Number.parseInt(p1, 16))
    )
  )
}

export const sleep = (ms: number) =>
  new Promise(r => {
    setTimeout(r, ms)
  })

/** 引数のtargetの要素内にidがある場合はtrueを返す */
export const hasElementInId = (target: EventTarget, id: string) => {
  let targetElement = target as HTMLElement

  while (targetElement) {
    if (targetElement.id === id) {
      return true
    }
    targetElement = targetElement.parentNode as HTMLElement
  }
  return false
}

/** 引数のtargetの要素内にclassNameがある場合はtrueを返す */
export const hasElementInClass = (target: EventTarget, className: string) => {
  let targetElement = target as HTMLElement

  while (targetElement) {
    if (targetElement.classList?.contains(className)) {
      return true
    }
    targetElement = targetElement.parentNode as HTMLElement
  }
  return false
}

/** 都道府県名を比較しソートを行う */
export function comparePrefectures(a: string | undefined, b: string | undefined): number {
  // 都道府県名を取得（未設定の場合は空文字に）
  const prefectureNameA = a || ''
  // eslint-disable-next-line unicorn/prefer-default-parameters
  const prefectureNameB = b || ''

  // 都道府県名をひらがなに変換
  const prefectureHiraganaA = prefectureMap[prefectureNameA]
  const prefectureHiraganaB = prefectureMap[prefectureNameB]

  // 有効な都道府県名かどうかを判定
  const isKnownPrefectureA = prefectureHiraganaA !== undefined
  const isKnownPrefectureB = prefectureHiraganaB !== undefined

  // 都道府県名が未設定かどうかを判定
  const isEmptyPrefectureA = prefectureNameA === ''
  const isEmptyPrefectureB = prefectureNameB === ''

  // 1. 未設定のデータを最後に配置
  if (isEmptyPrefectureA && !isEmptyPrefectureB) {
    return 1
  }
  if (!isEmptyPrefectureA && isEmptyPrefectureB) {
    return -1
  }
  if (isEmptyPrefectureA && isEmptyPrefectureB) {
    return 0
  }

  // 2. 有効な都道府県名を優先して比較
  if (isKnownPrefectureA && isKnownPrefectureB) {
    return prefectureHiraganaA.localeCompare(prefectureHiraganaB, 'ja')
  }
  if (isKnownPrefectureA) {
    return -1
  }
  if (isKnownPrefectureB) {
    return 1
  }

  // 3. 不正なデータ同士の比較
  return prefectureNameA.localeCompare(prefectureNameB, 'ja')
}

/**
 * compassへSSOで戻る際のリダイレクト先URLを解決する
 * toはcompass管理画面（RoovCompassUrl）配下のパス、またはcompassと同一オリジンの絶対URLを想定。
 * 同一オリジン以外の絶対URLはworks_tokenの漏えい防止のため許可せずundefinedを返す
 */
export const resolveCompassSsoRedirectUrl = (to: string): string | undefined => {
  // 不正なエンコーディング（例: %DD）で例外が親に伝播しないようガードする
  let toPath: string
  try {
    toPath = decodeURIComponent(to)
  } catch {
    return undefined
  }

  // 相対パス（RoovCompassUrlのパス部分が落ちないよう文字列連結する）
  if (toPath.startsWith('/')) {
    return toPath.startsWith('//') || toPath.includes('\\')
      ? undefined
      : `${process.env.RoovCompassUrl}${toPath}`
  }

  // 絶対パス（compassのみ許容）
  try {
    const resolved = new URL(toPath)
    return resolved.origin === new URL(`${process.env.RoovCompassUrl}`).origin
      ? resolved.toString()
      : undefined
  } catch {
    return undefined
  }
}
